import React, { useEffect } from "react";
import { observer } from "mobx-react";
import { fundraiserStore } from "../stores/FundraiserStore";
import FundraiserDashboard from "./FundraiserDashboard";
import FundraiserDetails from "./FundraiserDetails";
import FundraiserApp from "./FundraiserApp";
import FundraiserBuilder from "./FundraiserBuilder";
import { PromoterDashboardPage } from "./promoter-dashboard";
import FundraiserSettings from "./FundraiserSettings";
import FundraiserGallery from "./FundraiserGallery";
import GallerySettings from "./GallerySettings";
import { PromoterInvitePage } from "./promoter-invite";
import {
  AuthService,
  Loader,
  NoPermissionDialog,
  UtilityMethods,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import FindAccount from "./FindAccount";
import { EmbedFundraiserView } from "./EmbedFundraiserView";
import { FundraiserEmbedViewType } from "../enums";
import {
  Navigate,
  NavigateFunction,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { FundraiserFontOverride } from "./FundraiserFontOverride";
import {
  ApplicationSettingsStore,
  rolesAndPermissionStore,
} from "@mindmemobile/mm-payment-modules";

type IFundraiserRoutesProps = {
  navigate: NavigateFunction;
};
const FundraiserRoutes = observer(({ navigate }: IFundraiserRoutesProps) => {
  // Parse promoter ID from hash parameters
  const hash = window.location.hash;
  const params = useParams();
  const split = ((params && params["*"]) || "").split("/");
  let fundraiserId: string | null = split[split.length - 1] || null;
  const urlParams = new URLSearchParams(window.location.search);
  let promoterIdFromQueryString: string | null = urlParams.get("PID");

  // Parse URL hash for promoter admin routing
  // eslint-disable-next-line no-useless-escape
  const promoterAdminMatch = hash.match(/^#\/promoter\/([^\/]+)\/([^\/]+)$/);

  const pathname = window.location.pathname;
  // const fundraiserPathMatch = pathname.match(/^\/{1,2}fundraiser\/([^\/]+)$/);
  // const fundraiserName =
  //   fundraiserPathMatch && fundraiserPathMatch.length > 2
  //     ? fundraiserPathMatch[2]
  //     : "";
  //embed types
  function extractEmbedType() {
    const embedMatch = pathname.match(
      /^\/fundraiser\/embed\/([^\/]+)\/([^\/\?]+)/
    );
    if (!embedMatch) return null;
    return {
      type: embedMatch[1] as FundraiserEmbedViewType,
      fundraiserId: embedMatch[2],
    };
  }

  //const galleryMatch = pathname.match(/^\/fundraiser\/gallery\/([^/]+)$/);
  // const isGalleryRouting = galleryMatch ? true : false;

  if (promoterAdminMatch) {
    const [, promoterId1, fundraiserId1] = promoterAdminMatch;
    promoterIdFromQueryString = promoterId1 || "";
    fundraiserId = fundraiserId1 || "";
  }

  if (
    fundraiserId &&
    fundraiserStore.selectedFundraiserId !== fundraiserId &&
    UtilityMethods.isValidObjectId(fundraiserId)
  ) {
    fundraiserStore.setSelectedFundraiser(fundraiserId);
    !pathname.includes(fundraiserId) && navigate(`/fundraiser/${fundraiserId}`);
  }
  const embedType = extractEmbedType();
  const selectedFundraiserId = fundraiserStore.selectedFundraiserId;
  promoterIdFromQueryString =
    (UtilityMethods.isValidObjectId(promoterIdFromQueryString || "")
      ? promoterIdFromQueryString
      : null) ||
    fundraiserStore.selectedPromoterId ||
    null;
  fundraiserId =
    (UtilityMethods.isValidObjectId(fundraiserId || "")
      ? fundraiserId
      : null) ||
    selectedFundraiserId ||
    null;

  if (ApplicationSettingsStore.isLoadingInProgress) {
    return <>{new Loader().loaderComponent}</>;
  }

  return (
    <>
      <FundraiserFontOverride />{" "}
      {/* Same "insufficient rights" dialog the payment shell shows; canManageFundraiser opens it. */}
      <NoPermissionDialog
        showPopup={rolesAndPermissionStore.showPopup}
        title={rolesAndPermissionStore.title}
        message={rolesAndPermissionStore.message}
        togglePermissionPopup={(value) =>
          rolesAndPermissionStore.togglePermissionPopup(value)
        }
      />
      <Routes>
        <Route path="/" element={<Navigate to="/fundraiser/dashboard" />} />
        <Route path={`/find-account`} element={<FindAccount />} />
        <Route path={`/dashboard`} element={<FundraiserDashboard />} />
        <Route path={`/fundraiser-details`} element={<FundraiserDetails />} />
        <Route
          path={`/builder`}
          element={
            <FundraiserBuilder fundraiserId={fundraiserId || undefined} />
          }
        />
        <Route
          path={`/promoter-dashboard/*`}
          element={
            <PromoterDashboardPage
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                undefined
              }
              fundraiserId={fundraiserId || ""}
              showBackButton={AuthService.mindmeAccountDetails != null}
              // promoterId={"6749975e1a00e28461f45aef"}
              // fundraiserId={"68edeb52d7394759a80cc040"}
            />
          }
        />
        {/* <Route
          path={`/settings`}
          element={
            <FundraiserSettings aiSettings={fundraiserStore.openAISettings} />
          }
        /> */}
        <Route
          path={`/embed/*`}
          element={
            embedType && (
              <EmbedFundraiserView
                type={embedType.type}
                fundraiserId={embedType.fundraiserId}
                promoterId={
                  promoterIdFromQueryString ||
                  fundraiserStore.selectedPromoterId ||
                  ""
                }
              />
            )
          }
        />
        <Route
          path={`/gallery`}
          element={
            <FundraiserGallery shortUniqueId={AuthService.shortUniqueId} />
          }
        />
        <Route
          path={`/gallery/:galleryID`}
          element={
            <FundraiserGallery
              isPublicView={true}
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                ""
              }
            />
          }
        />
        <Route
          path={`/gallery/:shortUniqueId/:galleryName`}
          element={
            <FundraiserGallery
              isPublicView={true}
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                ""
              }
            />
          }
        />
        <Route path={`/gallery-settings`} element={<GallerySettings />} />
        <Route
          path={`/promoter-invite/:fundraiserID`}
          element={
            <PromoterInvitePage
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                undefined
              }
              fundraiser={fundraiserStore.selectedFundraiser}
              showBackButton={AuthService.mindmeAccountDetails != null}
            />
          }
        />
        <Route
          path="/:fundraiserID"
          element={
            <FundraiserApp
              fundraiserId={fundraiserId || undefined}
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                undefined
              }
              isPreview={
                AuthService.mindmeAccountDetails != null
                // ||              (!isGalleryRouting && !extractEmbedType())
              }
            />
          }
        />
        <Route
          path="/:shortUniqueId/:fundraiserID?"
          element={
            <FundraiserApp
              fundraiserId={fundraiserId || undefined}
              promoterId={
                promoterIdFromQueryString ||
                fundraiserStore.selectedPromoterId ||
                undefined
              }
              isPreview={
                AuthService.mindmeAccountDetails != null
                // ||              (!isGalleryRouting && !extractEmbedType())
              }
            />
          }
        />
      </Routes>
    </>
  );
});

export default withRouter(FundraiserRoutes);
